<template>
  <div
    class="cat-aria-datatable-announcer"
    :class="!visualMode ? 'screen-reader-text' : ''"
    aria-live="polite"
    aria-atomic="true"
    v-html="changeLogHtml"
  ></div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

interface LineItem {
  [key: string]: any;
}

interface ChangeItem {
  id: string | number;
  [key: string]: any; // To allow for dynamic properties accessed via this.announceableLabel
}

interface ChangedItems<T extends ChangeItem> {
  added: T[];
  modified: T[];
  removed: T[];
  [key: string]: any; // To allow for other properties like 'total'
}

export default defineComponent({
  name: "CatAriaDatatableAnnouncer",
  props: {
    lineItems: {
      type: Array<LineItem>,
      description: "List of line items",
      default: [],
      required: true
    },
    announceableLabel: {
      type: String,
      description: "Describes what key label to announce",
      required: true
    },
    announceableValue: {
      type: String,
      description: "Describes what key value to announce",
      required: true
    },
    footerRowKey: {
      type: String,
      description: "Declares the key of the footer row for tracking purposes",
      default: "total",
      required: false
    },
    actionKeys: {
      type: Object,
      description: "Text keys for the different action events",
      required: true
    },
    visualMode: {
      type: Boolean,
      default: false,
      description:
        "Indicates whether the text should be visually announced, this can be useful for debugging or demonstrating this component."
    }
  },
  data: function () {
    return {
      changeLog: [] as string[]
    };
  },
  watch: {
    trackedLineItems: {
      handler(newItems, oldItems) {
        if (!oldItems) {
          // First render, don't announce anything
          return;
        }

        const oldLength = oldItems ? oldItems.length : 0;
        const indexChangedBy = newItems.length - oldLength;

        let changedItems: ChangedItems<ChangeItem> = {
          added: [],
          modified: [],
          removed: []
        };

        // Check for changes in the arrays
        let removed: any[] = [];
        let added: any[] = [];
        let modified: any[] = [];

        // If items were added
        if (indexChangedBy > 0) {
          // New items are at the end of the array in our test case
          for (let i = oldItems.length; i < newItems.length; i++) {
            added.push(newItems[i]);
          }
        }

        // If items were removed
        if (indexChangedBy < 0) {
          // Use our existing method for removals since it's more complex
          const removedItems = this.findChangedItems(
            oldItems,
            newItems,
            "removed"
          );
          removed = removedItems.removed;
        }

        // Check for modifications in common items
        const minLength = Math.min(oldItems.length, newItems.length);
        for (let i = 0; i < minLength; i++) {
          // Skip checking items that would be after an insertion point
          // when the array grew (to avoid false positives)
          if (indexChangedBy > 0 && i >= oldItems.length) {
            continue;
          }

          if (this.hasRelevantChanges(oldItems[i], newItems[i])) {
            modified.push(newItems[i]);
          }
        }

        // Combine all changes
        changedItems = {
          added,
          modified,
          removed
        };

        // Completely recreate the changelog each time
        this.changeLog = this.buildChangelog(changedItems);
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    trackedLineItems: function () {
      if (!this.lineItems || this.lineItems.length <= 0) {
        return [];
      }
      return this.lineItems.map((item, index) => {
        return { ...item, id: index };
      });
    },
    changeLogHtml: function () {
      // for each item wrap in <p> tags
      return this.changeLog.map((item) => `<p>${item}</p>`).join("");
    }
  },
  methods: {
    buildChangelog<T extends ChangeItem>(
      changedItems: ChangedItems<T>
    ): string[] {
      const log: string[] = [];
      const footerRows = {
        added: null as T | null,
        modified: null as T | null,
        removed: null as T | null
      };

      // Process all items, with special handling for footer rows
      Object.entries(changedItems).forEach(([type, items]) => {
        // Skip if not a valid change type
        if (type !== "added" && type !== "modified" && type !== "removed") {
          return;
        }

        const actionMap: Record<string, string> = {
          added: "added",
          modified: "modified",
          removed: "removed"
        };

        const itemsArray = items as T[];

        // Process all items, including footer rows
        itemsArray.forEach((item) => {
          const label = item[this.announceableLabel] || "";
          const value = item[this.announceableValue] || "";

          // If this is a footer row, also store it separately
          if (this.isFooterRow(item)) {
            footerRows[type as keyof typeof footerRows] = item;
          }

          let actionKey = this.actionKeys[type];
          const labelAndValue = `${label}${value ? ": " + value : ""}`;

          let logItem = actionKey.includes("{0}")
            ? actionKey.replace("{0}", labelAndValue)
            : `${labelAndValue} ${actionKey}`;

          // Add all items to the log, including footer rows
          log.push(logItem);
        });
      });

      return log;
    },

    /**
     * Finds items that have been added, modified, or removed between two arrays
     * Uses different strategies based on the detected action type
     */
    findChangedItems<T extends { id: number }>(
      oldArray: Array<T>,
      newArray: Array<T>,
      action: "added" | "modified" | "removed" = "modified"
    ): ChangedItems<T> {
      if (
        !oldArray ||
        !newArray ||
        !Array.isArray(oldArray) ||
        !Array.isArray(newArray)
      )
        return { added: [], modified: [], removed: [] };

      const result: ChangedItems<T> = {
        added: [],
        modified: [],
        removed: []
      };

      // Handle each action type differently
      if (action === "modified") {
        // For modifications (same length arrays), compare items at the same positions
        for (let i = 0; i < newArray.length; i++) {
          const newItem = newArray[i];
          const oldItem = oldArray[i];

          if (this.hasRelevantChanges(oldItem, newItem)) {
            result.modified.push(newItem);
          }
        }
      } else if (action === "removed") {
        // For removals, we need to find which item was removed
        // Then also check if remaining items were modified

        // First, find the removed item(s)
        // The length has decreased, so oldArray has more items
        const oldLen = oldArray.length;
        const newLen = newArray.length;
        const removedCount = oldLen - newLen;

        // Simple approach for single item removal
        if (removedCount === 1) {
          // Find the first position where items differ
          let removedIndex = 0;
          while (
            removedIndex < newLen &&
            !this.hasRelevantChanges(
              oldArray[removedIndex],
              newArray[removedIndex]
            )
          ) {
            removedIndex++;
          }

          // The item at removedIndex in oldArray was removed
          result.removed.push(oldArray[removedIndex]);

          // Check if items after the removal point were modified
          for (let i = removedIndex; i < newLen; i++) {
            const newItem = newArray[i];
            const oldItem = oldArray[i + 1]; // +1 offset due to removal

            if (this.hasRelevantChanges(oldItem, newItem)) {
              result.modified.push(newItem);
            }
          }
        } else {
          // Fallback for multiple item removal
          // We'll do a simpler comparison that will at least catch the
          // fact that items were removed
          oldArray.forEach((oldItem) => {
            const matchesAnyNew = newArray.some(
              (newItem) => !this.hasRelevantChanges(oldItem, newItem)
            );

            if (!matchesAnyNew) {
              result.removed.push(oldItem);
            }
          });
        }
      } else if (action === "added") {
        // For additions, we need to find which item was added
        // Then also check if existing items were modified

        // First, find the added item(s)
        // The length has increased, so newArray has more items
        const oldLen = oldArray.length;
        const newLen = newArray.length;
        const addedCount = newLen - oldLen;

        // Simple approach for single item addition
        if (addedCount === 1) {
          // Find the first position where items differ
          let addedIndex = 0;
          while (
            addedIndex < oldLen &&
            !this.hasRelevantChanges(oldArray[addedIndex], newArray[addedIndex])
          ) {
            addedIndex++;
          }

          // The item at addedIndex in newArray was added
          result.added.push(newArray[addedIndex]);

          // Check if items after the addition point were modified
          for (let i = addedIndex; i < oldLen; i++) {
            const oldItem = oldArray[i];
            const newItem = newArray[i + 1]; // +1 offset due to addition

            if (this.hasRelevantChanges(oldItem, newItem)) {
              result.modified.push(newItem);
            }
          }
        } else {
          // Fallback for multiple item addition
          // We'll do a simpler comparison that will at least catch the
          // fact that items were added
          newArray.forEach((newItem) => {
            const matchesAnyOld = oldArray.some(
              (oldItem) => !this.hasRelevantChanges(oldItem, newItem)
            );

            if (!matchesAnyOld) {
              result.added.push(newItem);
            }
          });
        }
      }

      // Special handling for footer row
      this.handleFooterRowChanges(oldArray, newArray, result);

      return result;
    },

    /**
     * Determines if a row is a footer row based on its label
     * @param item The item to check
     * @returns True if this is a footer row
     */
    isFooterRow(item: any): boolean {
      const label = item[this.announceableLabel] || "";
      return label.toLowerCase() === this.footerRowKey;
    },

    /**
     * Handles special cases for footer rows (like totals)
     * Makes sure footer row changes are properly tracked
     */
    handleFooterRowChanges<T extends { id: number }>(
      oldArray: Array<T>,
      newArray: Array<T>,
      result: ChangedItems<T>
    ): void {
      // Find footer rows in both arrays
      const oldFooter = oldArray.find((item) => this.isFooterRow(item));
      const newFooter = newArray.find((item) => this.isFooterRow(item));

      // If footer exists in both arrays and has changed
      if (
        oldFooter &&
        newFooter &&
        this.hasRelevantChanges(oldFooter, newFooter)
      ) {
        // Check if this footer is already in one of our result arrays
        const alreadyIncluded =
          result.added.some((item) => item.id === newFooter.id) ||
          result.modified.some((item) => item.id === newFooter.id) ||
          result.removed.some((item) => item.id === oldFooter.id);

        // Only add if not already included
        if (!alreadyIncluded) {
          result.modified.push(newFooter);
        }
      }
    },

    /**
     * Compares two items to determine if they have relevant changes
     * Only checks properties that would trigger an announcement
     * @param oldItem The old version of the item
     * @param newItem The new version of the item
     * @returns True if there are differences in announcement-worthy properties
     */
    hasRelevantChanges(oldItem: any, newItem: any): boolean {
      if (!oldItem || !newItem) return true;

      // Compare only the properties that should trigger an announcement
      // This prevents unnecessary announcements for internal property changes
      const relevantProps = [this.announceableLabel, this.announceableValue];

      return relevantProps.some(
        (prop) =>
          oldItem[prop] !== newItem[prop] &&
          (oldItem[prop] !== undefined || newItem[prop] !== undefined)
      );
    }
  }
});
</script>
